import {
  ErrorManagerContext,
  ErrorResponse,
  NotificationData,
} from './ErrorManager';
import { useCallback, useContext, useMemo } from 'react';
import Notification from 'components/atoms/Notification/Notification';

function isErrorResponse(error: unknown): error is ErrorResponse {
  return (
    (error as ErrorResponse).fieldErrors !== undefined ||
    (error as ErrorResponse).name !== undefined
  );
}

export const useNotifications = () => {
  const {
    addNotification,
    notificationsData,
    removeNotification,
    errorToNotifications,
  } = useContext(ErrorManagerContext);

  const handleError = useCallback(
    (
      error: unknown,
      options: Omit<NotificationData, 'id'> = {
        type: 'error',
        message: '',
      },
    ) => {
      if (isErrorResponse(error)) {
        const errorsObj = errorToNotifications(error);
        errorsObj.forEach((err: Omit<NotificationData, 'id'>) => {
          const newErr = { ...options, ...err };
          addNotification(newErr);
        });
      } else {
        addNotification({
          ...options,
          type: 'error',
          message: '',
        });
      }
    },
    [addNotification, errorToNotifications],
  );

  const notifications = useMemo(
    () =>
      notificationsData.map((notification) => (
        <Notification
          key={notification.id}
          type={notification.type}
          text={notification.message}
          onClose={() => removeNotification(notification.id)}
        />
      )),
    [notificationsData, removeNotification],
  );

  return {
    notificationsData,
    notifications,
    addNotification,
    removeNotification,
    handleError,
  };
};
